import { useCallback, useEffect, useState } from 'react';

import { type Flow } from '../types';
import { findAllowedURLParams } from '../utils';
import { useCurrentPath } from './useCurrentPath';
import { useFlow } from './useFlow';
import { useFlowState } from './useFlowState';
import { useGoToStep } from './useGoToStep';

async function findPrevPath<S>(
  flow: Flow<S>,
  state: S,
  initialPath: string,
  currentPath: string = initialPath
): Promise<string> {
  const currentStep = flow[currentPath];
  const nextPath = await currentStep.next(state);
  return nextPath === initialPath
    ? currentPath
    : await findPrevPath(flow, state, initialPath, nextPath);
}

export function useGoToPrevStep(): [
  { error?: Error; loading: boolean },
  () => void
] {
  const currentPath = useCurrentPath();
  const [initiated, setInitiated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error>();
  const { value: state } = useFlowState();
  const { value: flow } = useFlow();
  const { goToStep } = useGoToStep();
  const urlParams = findAllowedURLParams();

  const findAndNavigate = useCallback(async () => {
    try {
      const prevPath = await findPrevPath(flow, state, currentPath);
      goToStep(prevPath, {
        queryParams: urlParams,
      });
    } catch (e) {
      setError(new Error('Could not find prev step'));
    } finally {
      setLoading(false);
    }
  }, [flow, state, currentPath, goToStep, urlParams]);

  const goToPrevStep = useCallback(() => {
    setLoading(true);
    setInitiated(true);
  }, [setInitiated, setLoading]);

  useEffect(() => {
    if (initiated) {
      setInitiated(false);
      void findAndNavigate();
    }
  }, [findAndNavigate, initiated]);

  return [{ error, loading }, goToPrevStep];
}
